import React, { Component } from 'react';
import { Modal } from 'antd';
import moment from 'moment';
import Axios from 'axios';
import {
  CONCEPTION_OPTIONS,
  DELIVERY_OPTIONS,
  PREGNANCY_OPTIONS,
  OUTCOME_OPTIONS,
  PLACE_OF_DELIVERY_OPTIONS,
  Get_Previous_Pregnancy_Details,
} from '../../../utils/constant';

// Gender options for a fetus's birth details (same mapping as PreviousPregnancyModal).
const GENDER_OPTIONS = [
  { val: '1', label: 'Male' },
  { val: '2', label: 'Female' },
  { val: '3', label: 'Other' },
];

/**
 * Read-only "Previous Pregnancy Details" table for the ANC card page and the ANC view page.
 *
 * This is the display half of the obs/gynae PreviousPregnancyModal: it fetches the patient's
 * previous-pregnancy records by patient_id and renders the same records table (clicking a row
 * opens a read-only detail view with the per-fetus table). It intentionally has NO add/edit
 * form — the ANC pages only show the data; capturing it still happens in the obs/gynae screen.
 */
class PreviousPregnancyDetails extends Component {
  constructor(props) {
    super(props);
    this.state = {
      records: [],
      // When set, the detail modal shows this record's full details.
      viewRecord: null,
    };
  }

  componentDidMount() {
    this.fetchRecords();
  }

  componentDidUpdate(prevProps) {
    // Re-fetch when the patient changes (e.g. the ANC page loads a different patient).
    if (prevProps.patientId !== this.props.patientId) {
      this.fetchRecords();
    }
  }

  fetchRecords = async () => {
    const patient_id = this.props.patientId;
    if (!patient_id) return;
    try {
      const res = await Axios.post(Get_Previous_Pregnancy_Details, { patient_id });
      if (res.data && res.data.status) {
        this.setState({ records: res.data.data || [] });
      } else {
        this.setState({ records: [] });
      }
    } catch (err) {
      // Non-blocking: leave the table empty on fetch failure.
      this.setState({ records: [] });
    }
  }

  labelFor = (options, val) => {
    const found = options.find((o) => String(o.val) === String(val));
    return found ? found.label : '';
  }

  // Builds the outer-table "Gender" summary from a record's fetus birth details.
  // Single fetus with a gender -> just the gender label (e.g. "Male").
  // Multiple fetuses -> "Fetus 1 : Male, Fetus 2 : Female".
  genderSummary = (rec) => {
    const fetuses = (rec && rec.fetuses) || [];
    const withGender = fetuses.filter((f) => f && f.delivery_gender != null && f.delivery_gender !== '');
    if (withGender.length === 0) return '-';
    if (withGender.length === 1) {
      return this.labelFor(GENDER_OPTIONS, withGender[0].delivery_gender) || '-';
    }
    return withGender
      .map((f, i) => `Fetus ${f.fetus_no || (i + 1)} : ${this.labelFor(GENDER_OPTIONS, f.delivery_gender) || '-'}`)
      .join(', ');
  }

  // Read-only detail view for a single record (opened by clicking a row). Mirrors the
  // obs/gynae PreviousPregnancyModal's view, so the ANC pages show the same information.
  renderViewModal = () => {
    const rec = this.state.viewRecord;
    if (!rec) return null;
    const boolAbn = (v) => (v === true || v === 'true');
    return (
      <Modal
        id="anc_previous_pregnancy_view"
        className="previous_pregnancy_view"
        visible={!!rec}
        bodyStyle={{ padding: '20px' }}
        closable={false}
        width="95vw"
        style={{ maxWidth: '1600px', top: 20 }}
        title={`Pregnancy Details${rec.pregnancy_year ? ' - ' + rec.pregnancy_year : ''}`}
        footer={[
          <div className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }} key="footer">
            <button
              className="btn btn-outline-secondary px-5"
              onClick={() => this.setState({ viewRecord: null })}
            >
              CLOSE
            </button>
          </div>,
        ]}
      >
        <div className="container-fluid emrfrm">
          <div className="row">
            <div className="col-md-4 mb-2"><strong>Year:</strong> {rec.pregnancy_year || '-'}</div>
            <div className="col-md-4 mb-2"><strong>Mode of Conception:</strong> {this.labelFor(CONCEPTION_OPTIONS, rec.mode_of_conception) || '-'}</div>
            <div className="col-md-4 mb-2">
              <strong>Mode of Delivery:</strong> {this.labelFor(DELIVERY_OPTIONS, rec.mode_of_delivery) || '-'}
              {rec.mode_of_delivery === '2' && rec.caesarean_section_type ? ` (${rec.caesarean_section_type})` : ''}
            </div>
            <div className="col-md-4 mb-2"><strong>Place of Delivery:</strong> {this.labelFor(PLACE_OF_DELIVERY_OPTIONS, rec.place_of_delivery) || '-'}</div>
            <div className="col-md-4 mb-2"><strong>Outcome:</strong> {this.labelFor(OUTCOME_OPTIONS, (rec.fetuses && rec.fetuses[0] && rec.fetuses[0].delivery_outcome) || rec.delivery_outcome) || '-'}</div>
            <div className="col-md-4 mb-2"><strong>Pregnancy Type:</strong> {this.labelFor(PREGNANCY_OPTIONS, rec.pregnancy_type) || '-'}</div>
            <div className="col-md-4 mb-2"><strong>No. of Fetus:</strong> {rec.no_of_fetus || '-'}</div>
            {rec.mode_of_delivery === '2' && (
              <div className="col-md-8 mb-2"><strong>Indication for C-Section:</strong> {rec.indication_for_c_section || '-'}</div>
            )}
            <div className="col-md-4 mb-2"><strong>Added By:</strong> {rec.added_by_name || (rec.source_table === 'anc' ? 'ANC Record' : '-')}</div>
          </div>

          {(rec.fetuses || []).length > 0 && (
            <div className="table-responsive mt-3">
              <style>{`#anc-previous-pregnancy-fetus-table th, #anc-previous-pregnancy-fetus-table td { white-space: nowrap !important; word-break: keep-all; }`}</style>
              <table
                id="anc-previous-pregnancy-fetus-table"
                className="table table-bordered"
                style={{ whiteSpace: 'nowrap', width: 'max-content', minWidth: '100%' }}
              >
                <thead style={{ whiteSpace: 'nowrap' }}>
                  <tr style={{ whiteSpace: 'nowrap' }}>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>FETUS</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>OUTCOME</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>GENDER</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>BIRTH DATE &amp; TIME</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>WEIGHT (kg)</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>HEIGHT (cm)</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>HEAD CC (cm)</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>CONGENITAL ABNORMALITY</th>
                  </tr>
                </thead>
                <tbody>
                  {rec.fetuses.map((f, i) => {
                    const hasData = f && (f.delivery_bith_date_time || f.delivery_gender || f.delivery_birth_weight || f.delivery_birth_height || f.delivery_birth_head_cc || f.delivery_congenital_abnormality);
                    if (!hasData) {
                      return null;
                    }
                    return (
                      <tr key={i}>
                        <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{f.fetus_no || (i + 1)}</td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{this.labelFor(OUTCOME_OPTIONS, f.delivery_outcome) || '-'}</td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{this.labelFor(GENDER_OPTIONS, f.delivery_gender) || '-'}</td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{f.delivery_bith_date_time ? moment(f.delivery_bith_date_time, 'YYYY-MM-DD HH:mm:ss').format('DD MMM, YYYY hh:mm A') : '-'}</td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{f.delivery_birth_weight || '-'}</td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{f.delivery_birth_height || '-'}</td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>{f.delivery_birth_head_cc || '-'}</td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all' }}>
                          {boolAbn(f.delivery_congenital_abnormality)
                            ? `Yes${f.delivery_congenital_abnormality_remarks ? ' - ' + f.delivery_congenital_abnormality_remarks : ''}`
                            : 'No'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    );
  }

  render() {
    const { records } = this.state;
    return (
      <>
        <div className='row mt-4'>
          <div className='col-12'>
            <span className='title'>PREVIOUS PREGNANCY DETAILS</span>
          </div>
          <div className='col-12'>
            <div className="table-responsive">
              <style>{`#anc-prev-pregnancy-table, #anc-prev-pregnancy-table * { white-space: nowrap !important; word-break: keep-all; }`}</style>
              <table
                className="table table-bordered mt-3"
                id="anc-prev-pregnancy-table"
                style={{ whiteSpace: 'nowrap', width: 'max-content', minWidth: '100%' }}
              >
                <thead>
                  <tr>
                    <td className="text-center">YEAR</td>
                    <td className="text-center">MODE OF CONCEPTION</td>
                    <td className="text-center">MODE OF DELIVERY</td>
                    <td className="text-center">PLACE OF DELIVERY</td>
                    <td className="text-center">OUTCOME</td>
                    <td className="text-center">PREGNANCY TYPE</td>
                    <td className="text-center">GENDER</td>
                    <td className="text-center">INDICATION FOR C-SECTION</td>
                    <td className="text-center">ADDED BY</td>
                  </tr>
                </thead>
                <tbody>
                  {records.length === 0 ? (
                    <tr>
                      <td className="text-center" colSpan={9}>No data available</td>
                    </tr>
                  ) : (
                    records.map((rec, i) => (
                      <tr
                        key={i}
                        style={{ cursor: 'pointer' }}
                        title="Click to view details"
                        onClick={() => this.setState({ viewRecord: rec })}
                      >
                        <td className="text-center">{rec.pregnancy_year}</td>
                        <td className="text-center">{this.labelFor(CONCEPTION_OPTIONS, rec.mode_of_conception)}</td>
                        <td className="text-center">
                          {this.labelFor(DELIVERY_OPTIONS, rec.mode_of_delivery)}
                          {rec.mode_of_delivery === '2' && rec.caesarean_section_type ? ` (${rec.caesarean_section_type})` : ''}
                        </td>
                        <td className="text-center">{this.labelFor(PLACE_OF_DELIVERY_OPTIONS, rec.place_of_delivery)}</td>
                        <td className="text-center">
                          {this.labelFor(OUTCOME_OPTIONS, (rec.fetuses && rec.fetuses[0] && rec.fetuses[0].delivery_outcome) || rec.delivery_outcome) || '-'}
                        </td>
                        <td className="text-center">{this.labelFor(PREGNANCY_OPTIONS, rec.pregnancy_type)}</td>
                        <td className="text-center">{this.genderSummary(rec)}</td>
                        <td className="text-center">{rec.indication_for_c_section}</td>
                        <td className="text-center">{rec.added_by_name || (rec.source_table === 'anc' ? 'ANC Record' : '')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {this.renderViewModal()}
      </>
    );
  }
}

export default PreviousPregnancyDetails;
